import { fieldModuleRegistry } from '@/forms/field-modules/registry';

import { config } from '../config';
import { shouldSkipFormChangeFieldKey } from '../hooks/form-change-handler-common';
import { FormFieldType } from '../typings';
import type {
  FileFormField,
  FormField,
  FormPrimitiveValues,
  SingleFormField,
} from '../typings';

import { addressLinkFieldId } from './addressLink';
import { getClearedFieldValue } from './clearedFieldValue';

/** A Formik key holding a user value, paired with the empty value its type takes. */
export type ClearableFieldKey = {
  key: string;
  clearedValue: FormPrimitiveValues;
};

/**
 * Keys a compound type stores outside the leaves its module reports.
 * A new compound type that keeps a value under its own id must be registered here.
 */
const ownValueKeyByType: Partial<
  Record<FormFieldType, (field: FormField) => string | undefined>
> = {
  // The '' | 'yes' | 'no' answer; the leaves are the detail fields under each answer.
  [FormFieldType.QuestionWithDetails]: field =>
    `${field.fieldId}${config.questionWithDetailsFieldSuffix}`,
  // The joined selection; the leaves are the individual checkboxes.
  [FormFieldType.CheckboxGroup]: field => field.fieldId,
  // The address source choice, which the server resets when it clears the group.
  [FormFieldType.FieldsGroup]: field =>
    field.type === FormFieldType.FieldsGroup && field.address?.options.length
      ? addressLinkFieldId(field.fieldId)
      : undefined,
};

/** Formik keys holding user values for this field and everything under it. */
export const collectClearableFieldKeys = (
  field: FormField,
): ClearableFieldKey[] => {
  const found = new Map<string, ClearableFieldKey>();

  // Recursing ourselves instead of reusing adaptFormDataToFormField gets us every intermediate
  // node, not only the leaves — a nested QuestionWithDetails would otherwise keep its answer.
  // It also skips naDetails: any item defining them reloads its section on tick anyway.
  const visit = (fields: FormField[]): Array<SingleFormField | FileFormField> =>
    fields.flatMap(node => {
      const key = ownValueKeyByType[node.type]?.(node);
      if (key) {
        found.set(key, { key, clearedValue: '' });
      }

      return fieldModuleRegistry
        .get(node.type)
        .flattenLeaves(node, { recurse: visit });
    });

  for (const leaf of visit([field])) {
    // A File keeps no value under its own id, so writing one would invent a key the change
    // tracker autosaves. Its uploads are cleared by the section reload instead.
    if (leaf.type === FormFieldType.File) continue;

    found.set(leaf.fieldId, {
      key: leaf.fieldId,
      clearedValue: getClearedFieldValue(leaf),
    });
  }

  return [...found.values()].filter(
    ({ key }) => !shouldSkipFormChangeFieldKey(key),
  );
};
